import React from 'react';
import PropTypes from 'prop-types';

import { Form } from 'react-bootstrap-5';

import ManagedInput from './index';
import classNames from 'classnames';
import { startCase } from 'lodash';

/**
 * ManagedFormGroup - A form group component with flexible label handling
 *
 * The `label` prop accepts three types of values:
 *
 * 1. String: Auto-generates a <label> element with the string content
 *    Example: label="Email Address"
 *
 * 2. React Element: Renders the element directly, allowing custom label markup
 *    Example: label={<Form.Label><Icon /> Name</Form.Label>}
 *    Note: When passing a custom element, you're responsible for the entire
 *    label rendering including required indicators, styling, etc.
 *
 * 3. null: Explicitly suppresses label rendering (no label will be shown)
 *    Example: label={null}
 *
 * If `label` is not provided (undefined), the component falls back to generating
 * a label from the `name` prop using startCase formatting.
 */
export default class ManagedFormGroup extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    feedback: PropTypes.node,
    feedbackType: PropTypes.string,
    hasError: PropTypes.bool,
    inputClassName: PropTypes.string,
    label: PropTypes.node,
    name: PropTypes.string,
    required: PropTypes.bool,
    showFullLabelBorder: PropTypes.bool,
    sublabel: PropTypes.node,
    type: PropTypes.string
  }

  render() {
    const className = classNames('form-group', this.props.className, {
      boolean: this.props.type === 'checkbox',
      'has-error': this.props.hasError,
      required: this.props.required,
      text: this.props.type === 'textarea'
    });

    return (
      <div className={className}>
        {['autocomplete', 'combobox', 'textarea'].includes(this.props.type)
          ? this.renderInputNoContainer()
          : this.renderInputContainer()
        }
      </div>
    );
  }

  renderLabel() {
    const { label, name, required, type, showFullLabelBorder } = this.props;

    // Return null if label is explicitly null (allows suppressing the label)
    if (label === null) {
      return null;
    }

    // If label is a React element, render it directly
    if (React.isValidElement(label)) {
      return label;
    }

    // Otherwise, generate label text from label prop or name prop
    let labelText;
    if (label) {
      labelText = label;
    } else if (name) {
      labelText = startCase(name);
    } else {
      labelText = '';
      /* eslint-disable no-console */
      console.warn('ManagedFormGroup requires label or name');
      /* eslint-enable no-console */
    }

    const labelClassNameObject = {
      text: type === 'textarea',
      'full-border': showFullLabelBorder
    };

    return <>
      <Form.Label className={classNames(labelClassNameObject)}>
        {labelText}
      </Form.Label>
      <div className="fs-small text-muted">{this.props.sublabel}</div>
    </>;
  }

  renderSublabel() {
  }

  renderInputContainer() {
    return <React.Fragment>
      {this.renderLabel()}
      <div className="form-input-container">
        {this.renderInput()}
      </div>
      {this.renderFeedback()}
    </React.Fragment>;
  }

  renderInputNoContainer() {
    return (
      <div className="d-flex flex-column gap-1">
        {this.renderLabel()}
        {this.renderInput()}
        {this.renderFeedback()}
      </div>
    );
  }

  renderInput() {
    const {
      children, className, feedback, feedbackType, hasError, inputClassName, label, required, ...inputProps
    } = this.props;
    const noFormControl = ['autocomplete', 'checkboxGroup', 'combobox', 'radioGroup', 'select'];
    const props = {
      // Only use label as aria-label if it's a string, not a React element
      ...(typeof label === 'string' && { 'aria-label': label }),
      ...inputProps,
      className: classNames(inputClassName, { 'form-control': !noFormControl.includes(inputProps.type) }),
    };

    return (
      <ManagedInput {...props}>
        {children}
      </ManagedInput>
    );
  }

  renderFeedback() {
    if (this.props.feedback && this.props.feedback !== false) {
      return (
        <Form.Control.Feedback className="d-block" type={this.props.feedbackType}>
          {this.props.feedback}
        </Form.Control.Feedback>
      );
    }
  }
}
